import { useLocalStorage } from './useLocalStorage';

export const useStudyGoals = () => {
  const [goalData, setGoalData] = useLocalStorage('studyGoals', {
    dailyGoal: 10, // Default: review 10 cards per day
    weeklyGoal: 70, // Default: review 70 cards per week
    dailyProgress: 0,
    weeklyProgress: 0,
    lastResetDate: null,
    weekStartDate: null,
    history: [] // { date, cardsReviewed, goalMet }
  });

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getWeekStartString = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as start of week
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff);
    return monday.toISOString().split('T')[0];
  };

  const resetIfNeeded = () => {
    const today = getTodayString();
    const weekStart = getWeekStartString();
    let updated = false;
    let newData = { ...goalData };

    // Reset daily progress if it's a new day
    if (goalData.lastResetDate !== today) {
      // Save yesterday's progress to history
      if (goalData.lastResetDate && goalData.dailyProgress > 0) {
        const historyEntry = {
          date: goalData.lastResetDate,
          cardsReviewed: goalData.dailyProgress,
          goalMet: goalData.dailyProgress >= goalData.dailyGoal
        };
        newData.history = [...(goalData.history || []), historyEntry].slice(-30); // Keep last 30 days
      }

      newData.dailyProgress = 0;
      newData.lastResetDate = today;
      updated = true;
    }

    // Reset weekly progress if it's a new week
    if (goalData.weekStartDate !== weekStart) {
      newData.weeklyProgress = 0;
      newData.weekStartDate = weekStart;
      updated = true;
    }

    if (updated) {
      setGoalData(newData);
      return newData;
    }

    return goalData;
  };

  const recordCardReview = () => {
    const currentData = resetIfNeeded();

    setGoalData({
      ...currentData,
      dailyProgress: currentData.dailyProgress + 1,
      weeklyProgress: currentData.weeklyProgress + 1
    });
  };

  const setDailyGoal = (goal) => {
    setGoalData({
      ...goalData,
      dailyGoal: goal
    });
  };

  const setWeeklyGoal = (goal) => {
    setGoalData({
      ...goalData,
      weeklyGoal: goal
    });
  };

  // Initialize reset check
  const currentData = resetIfNeeded();

  const dailyGoalMet = currentData.dailyProgress >= currentData.dailyGoal;
  const weeklyGoalMet = currentData.weeklyProgress >= currentData.weeklyGoal;
  const dailyProgressPercent = Math.min((currentData.dailyProgress / currentData.dailyGoal) * 100, 100);
  const weeklyProgressPercent = Math.min((currentData.weeklyProgress / currentData.weeklyGoal) * 100, 100);

  return {
    dailyGoal: currentData.dailyGoal,
    weeklyGoal: currentData.weeklyGoal,
    dailyProgress: currentData.dailyProgress,
    weeklyProgress: currentData.weeklyProgress,
    dailyGoalMet,
    weeklyGoalMet,
    dailyProgressPercent,
    weeklyProgressPercent,
    history: currentData.history || [],
    recordCardReview,
    setDailyGoal,
    setWeeklyGoal
  };
};
